// Registers the service worker and applies updates. A new version installs in
// the background and waits. If it is already waiting when the app opens,
// nothing is in progress yet, so it goes in straight away; one that arrives
// mid-session is offered as a reload rather than swapped in under a review.
// (The signed-out screen applies it at once too; see app.tsx.)

let waiting: ServiceWorker | null = null;
const listeners = new Set<() => void>();

function announce(w: ServiceWorker) {
  waiting = w;
  listeners.forEach((f) => f());
}

export function onUpdateReady(fn: () => void): () => void {
  listeners.add(fn);
  if (waiting) fn();
  return () => void listeners.delete(fn);
}

export function applyUpdate(): void {
  if (!waiting) return location.reload();
  waiting.postMessage('skipWaiting');
}

export async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
  const reg = await navigator.serviceWorker.register('/sw.js');
  // The first install has nothing to replace; only later ones are updates.
  const hadController = !!navigator.serviceWorker.controller;
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController || reloading) return;
    reloading = true;
    location.reload();
  });
  if (reg.waiting && hadController) {
    reg.waiting.postMessage('skipWaiting');
    return;
  }
  reg.addEventListener('updatefound', () => {
    const w = reg.installing;
    w?.addEventListener('statechange', () => {
      if (w.state === 'installed' && navigator.serviceWorker.controller) announce(w);
    });
  });
  // Installed apps can stay open for days; look for a new version now and then.
  setInterval(() => void reg.update().catch(() => {}), 60 * 60_000);
}
